import { useQuery } from '@tanstack/react-query';
import { Box, Button, Container, Grid, Hidden, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { trackCategoryEvent } from '../../../services/analytics';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  cursiveFont,
  straightFont,
  white,
  primary,
  secondary,
} from '../../../Constants';
import { getSiteConfig } from '../../../services/DbService';
import { paths } from '../../../routes/paths';
import SearchBar from '../../../components/SearchBar';
import useRouter from '../../../hooks/use-router';
import ICategoryCard from '../../../interfaces/ICategoryCard';
import { useTheme } from '../../../providers/ThemeProvider';

// Reusable Category Card Component with modern overlay
function CategoryCard({
  image,
  label,
  onClick,
  size = 'large',
}: {
  image: string;
  label: string;
  onClick: () => void;
  size?: 'large' | 'small';
}) {
  const isLarge = size === 'large';

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      style={{ height: '100%' }}
    >
      <Box
        onClick={onClick}
        sx={{
          position: 'relative',
          borderRadius: 3,
          overflow: 'hidden',
          cursor: 'pointer',
          aspectRatio: isLarge ? '4/5' : '1/1',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          transition: 'box-shadow 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
            '& img': {
              transform: 'scale(1.05)',
            },
            '& .overlay': {
              background: `linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)`,
            },
          },
        }}
      >
        <img
          src={image}
          alt={label}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.4s ease',
          }}
        />
        {/* Gradient Overlay */}
        <Box
          className="overlay"
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            top: 0,
            background: `linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.2) 40%, transparent 100%)`,
            transition: 'background 0.3s ease',
          }}
        />
        {/* Label */}
        <Typography
          sx={{
            position: 'absolute',
            bottom: isLarge ? 24 : 16,
            left: 0,
            right: 0,
            textAlign: 'center',
            color: white,
            fontFamily: straightFont,
            fontWeight: 700,
            fontSize: isLarge ? '1.25rem' : '0.9rem',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
          }}
        >
          {label}
        </Typography>
      </Box>
    </motion.div>
  );
}

export default function HomeHero() {
  const router = useRouter();
  const { colors } = useTheme();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Consolidated site config - reduces 5 collection reads to 1 document read
  const { data: siteConfig, error: siteConfigError } = useQuery(
    ['siteConfig'],
    getSiteConfig,
    { staleTime: Infinity } // Site config rarely changes
  );

  if (siteConfigError) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">Error loading content</Typography>
      </Box>
    );
  }

  const handleCategoryClick = (tagName: string) => {
    trackCategoryEvent('view', tagName);
    router.push(paths.recipe.tagName(tagName));
  };

  return (
    <Box sx={{ backgroundColor: colors.background, pt: { xs: 12, md: 14 }, pb: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        {/* Hero Tagline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
            <Typography
              variant="h2"
              sx={{
                fontFamily: straightFont,
                color: colors.text,
                fontWeight: 300,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                letterSpacing: '-0.5px',
                mb: 0.5,
              }}
            >
              No stories.
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontFamily: cursiveFont,
                color: secondary,
                fontWeight: 400,
                fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
              }}
            >
              Just recipes.
            </Typography>
          </Box>
        </motion.div>

        {/* Search Bar - Desktop Only */}
        <Hidden mdDown>
          <Box sx={{ maxWidth: 500, mx: 'auto', mb: 5 }}>
            <SearchBar />
          </Box>
        </Hidden>

        {/* Main Category Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {siteConfig?.homeHeroItems?.map((categoryCard: ICategoryCard, index: number) => (
            <Grid item key={categoryCard.id} xs={6} sm={6} md={3}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <CategoryCard
                  image={categoryCard.image}
                  label={categoryCard.tag}
                  onClick={() => handleCategoryClick(categoryCard.tag)}
                  size="large"
                />
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Secondary Category Cards */}
        <Grid container spacing={2} justifyContent="center" sx={{ mb: 4 }}>
          {siteConfig?.homeHeroSmallItems?.map((categoryCard: ICategoryCard, index: number) => (
            <Grid item xs={4} sm={4} md={2} key={categoryCard.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 + index * 0.08 }}
              >
                <CategoryCard
                  image={categoryCard.image}
                  label={categoryCard.tag}
                  onClick={() => handleCategoryClick(categoryCard.tag)}
                  size="small"
                />
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* View All Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            component={Link}
            to="/recipes"
            endIcon={<ArrowForwardIcon />}
            sx={{
              backgroundColor: primary,
              color: white,
              fontFamily: straightFont,
              fontWeight: 600,
              fontSize: '1rem',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: `0 4px 14px ${alpha(primary, 0.35)}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: alpha(primary, 0.9),
                boxShadow: `0 6px 20px ${alpha(primary, 0.45)}`,
                transform: 'translateY(-2px)',
              },
            }}
          >
            Browse All Recipes
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
